// The SELF Forum room: video for everyone, the co-moderator's panel beside it, and the
// moderator's buttons. All meeting logic lives in the agent; this page shows its state and
// sends commands ("co who Kirk") on the "cmd" data topic.
import { Room, RoomEvent, Track } from "livekit-client";
import { initShare, openPicker, sharing, stop as stopShare } from "./share.js";

const $ = id => document.getElementById(id);
const q = new URLSearchParams(location.search);
const enc = new TextEncoder(), dec = new TextDecoder();
const AGENT = "co-moderator";
let room, state = null, me = null, isCtl = false, bannerTimer, toastTimer, lastCur = null, lastStateAt = 0;

// ---------------------------------------------------------------- join

// A personal invite link carries everything after the "#" (never sent to any server):
//   https://forum.self.mizzi.com/#u=<livekit url>&t=<signed token for this member>
// Without one (local development) the page asks the dev server for a token by ?name=.
const hash = new URLSearchParams(location.hash.slice(1));
function tokenClaims(t) {
  try { return JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))); } catch { return {}; }
}
async function token() {
  if (hash.get("t")) {
    const t = hash.get("t"), c = tokenClaims(t);
    if (c.exp && c.exp * 1000 < Date.now()) throw new Error("this invite link has expired — ask Kalvin or Vadim for a new one");
    return { url: hash.get("u"), token: t, identity: c.sub, name: c.name || c.sub };
  }
  if (location.hostname !== "127.0.0.1" && location.hostname !== "localhost") throw new Error("open the room from your personal invite link");
  const r = await fetch(`token?${q.toString()}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json(); // {url, token, identity, name}
}

async function preview() {
  try {
    const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    $("preview").srcObject = s;
    return s;
  } catch { return null; }
}

async function join(prev) {
  const t = await token();
  me = t.identity;
  prev?.getTracks().forEach(tr => tr.stop());
  room = new Room({ adaptiveStream: true, dynacast: true });
  room.on(RoomEvent.TrackSubscribed, render)
      .on(RoomEvent.TrackUnsubscribed, render)
      .on(RoomEvent.ParticipantConnected, render)
      .on(RoomEvent.ParticipantDisconnected, render)
      .on(RoomEvent.LocalTrackPublished, render)
      .on(RoomEvent.LocalTrackUnpublished, render)
      .on(RoomEvent.TrackMuted, render).on(RoomEvent.TrackUnmuted, render)
      .on(RoomEvent.ActiveSpeakersChanged, speakers => {
        const ids = new Set(speakers.map(p => p.identity));
        document.querySelectorAll(".tile").forEach(el => el.classList.toggle("speaking", ids.has(el.dataset.id)));
      })
      .on(RoomEvent.DataReceived, onData)
      .on(RoomEvent.Disconnected, () => { $("app").classList.add("hidden"); $("join").classList.remove("hidden"); $("joinwho").textContent = "You left the forum."; });
  await room.connect(t.url, t.token);
  initShare(room, render);
  // Show the room as soon as we're connected. Camera and microphone come on in the background:
  // waiting on the browser's permission prompt left people stuck on "Joining as …" while already in.
  $("join").classList.add("hidden");
  $("app").classList.remove("hidden");
  if (!navigator.mediaDevices?.getDisplayMedia) $("share").classList.add("hidden"); // phones can't share a screen
  render();
  room.localParticipant.enableCameraAndMicrophone()
    .catch(() => room.localParticipant.setMicrophoneEnabled(true))
    .catch(() => toast("Your microphone is off: allow it in the browser's address bar, then press Unmute."))
    .finally(render);
  // Juno sends its state every second. Silence for 6 s means it's offline: say so plainly, so a
  // frozen clock or dead buttons never look like the room is broken.
  setInterval(() => {
    const juno = room.remoteParticipants.get(AGENT);
    const off = room.state === "connected" && (!juno || Date.now() - lastStateAt > 6000);
    $("offline").classList.toggle("hidden", !off || Date.now() - joinedAt < 8000);
  }, 1000);
}
const joinedAt = Date.now();

// ---------------------------------------------------------------- video grid

// Juno has no camera: her tile is her picture, which glows while she talks (the active-speaker
// handler adds .speaking like anyone else's). She never takes the big tile.
function junoTile(participant) {
  const el = document.createElement("div");
  el.className = "tile juno";
  el.dataset.id = participant.identity;
  el.innerHTML = '<img src="juno.jpg" alt=""><div class="name">Juno · AI</div>';
  return el;
}

function tileFor(participant, pub, screen = false) {
  const el = document.createElement("div");
  el.className = "tile" + (screen ? " screen" : "");
  el.dataset.id = participant.identity;
  const track = pub?.track;
  if (track && !pub.isMuted) {
    const v = track.attach();
    if (participant.isLocal && !screen) v.style.transform = "scaleX(-1)";
    el.appendChild(v);
  } else {
    el.innerHTML = `<div class="initial">${esc((participant.name || participant.identity)[0] || "?")}</div>`;
  }
  const label = document.createElement("div");
  label.className = "name";
  label.textContent = (participant.name || participant.identity) + (screen ? " · screen" : participant.isLocal ? " (you)" : "");
  el.appendChild(label);
  return el;
}

function render() {
  if (!room) return;
  const grid = $("grid");
  grid.innerHTML = "";
  const people = [room.localParticipant, ...room.remoteParticipants.values()].filter(p => p.identity !== AGENT);
  const curId = state && !state.onBreak && state.speaker ? identityOf(state.speaker) : null;
  const shares = people.map(p => [p, p.getTrackPublication(Track.Source.ScreenShare)]).filter(([, sc]) => sc?.track);
  const cams = people.map(p => {
    const t = tileFor(p, p.getTrackPublication(Track.Source.Camera));
    if (p.identity === curId) { t.classList.add("current"); t.insertAdjacentHTML("beforeend", '<div class="now">NOW</div>'); }
    return [p, t];
  });
  const juno = room.remoteParticipants.get(AGENT);
  if (juno) cams.push([juno, junoTile(juno)]);
  // A screen share, or the person whose turn it is, gets the big tile; everyone else a strip.
  const bigTile = shares.length ? tileFor(shares[0][0], shares[0][1], true) : curId ? cams.find(([p]) => p.identity === curId)?.[1] : null;
  grid.classList.toggle("stage", !!bigTile);
  if (bigTile) {
    const big = document.createElement("div"); big.className = "big"; big.appendChild(bigTile);
    const strip = document.createElement("div"); strip.className = "strip";
    for (const [, t] of cams) if (t !== bigTile) strip.appendChild(t);
    grid.append(big, strip);
  } else {
    for (const [, t] of cams) grid.appendChild(t);
  }
  // Everyone's audio, including the co-moderator's voice.
  for (const p of room.remoteParticipants.values()) {
    const a = p.getTrackPublication(Track.Source.Microphone)?.track;
    if (a && !a.attachedElements.length) document.body.appendChild(a.attach());
  }
  lastCur = curId;
  $("mic").textContent = room.localParticipant.isMicrophoneEnabled ? "Mute" : "Unmute";
  $("cam").textContent = room.localParticipant.isCameraEnabled ? "Stop video" : "Start video";
  $("share").textContent = sharing() ? "Stop sharing" : "Share";
}

function identityOf(member) {
  for (const p of [room.localParticipant, ...room.remoteParticipants.values()])
    if ((p.identity || "").toLowerCase() === (member || "").toLowerCase()) return p.identity;
  return null;
}

// ---------------------------------------------------------------- the co-moderator's state

function onData(payload, participant, _kind, topic) {
  let d; try { d = JSON.parse(dec.decode(payload)); } catch { return; }
  if (topic === "chat" && participant) return addChat(participant.name || participant.identity, String(d.text || "").slice(0, 500));
  if (participant && participant.identity !== AGENT) return; // only the co-moderator talks to the UI
  if (topic === "state") { lastStateAt = Date.now(); $("offline").classList.add("hidden"); state = d; isCtl = (d.controllers || []).some(c => c.toLowerCase() === (me || "").toLowerCase()); panel(); }
  if (topic === "flash") banner(d.title, d.sub);
  if (topic === "note") toast(d.text);
  if (topic === "cue") { cueText = d.text; cueAt = Date.now(); }
}

const fmt = ms => { const t = Math.round(Math.abs(ms) / 1000); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`; };

const clockTime = t => new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const mins = ms => `${Math.max(1, Math.round(ms / 60000))} min`;

// A moderator's big moves (skip ahead, jump on the agenda) take two taps, so a stray click
// can't throw the meeting forward. The first tap arms the button for 3 seconds.
let armed = null; // {cmd, until}
let cueText = "", cueAt = 0; // Juno's private note to this person (a minute on screen)
const isArmed = c => armed && armed.cmd === c && Date.now() < armed.until;

function panel() {
  const s = state;
  timeline(s);
  $("radar").innerHTML = (s.radar || []).length ? s.radar.map(t => `<li>${esc(t)}</li>`).join("") : '<li class="empty">Nothing yet</li>';
  // Moderator controls
  $("modpanel").classList.toggle("hidden", !isCtl);
  if (isCtl) {
    const skip = s.nextSeg ? `co goto ${s.nextSeg.id}` : null;
    $("skipbtn").disabled = !skip || !!s.ended;
    $("skipbtn").dataset.cmd = skip || "";
    $("skipbtn").classList.toggle("armed", !!skip && isArmed(skip));
    $("skipbtn").textContent = !skip ? "⏭ Last agenda item" : isArmed(skip) ? `Tap again to skip to ${s.nextSeg.title}` : `⏭ Skip to ${s.nextSeg.title}`;
    $("nextspk").classList.toggle("hidden", s.kind !== "updates" || !!s.onBreak);
    $("mutebtn").textContent = s.muted ? "🔊 Unmute voice" : "🔇 Mute voice";
    $("mutebtn").dataset.cmd = s.muted ? "co unmute" : "co mute";
    $("mutebtn").classList.toggle("on", !!s.muted);
    $("pausebtn").textContent = s.paused ? "▶ Resume Juno" : "⏸ Pause Juno";
    $("pausebtn").dataset.cmd = s.paused ? "co back" : "co pause";
    $("pausebtn").classList.toggle("on", !!s.paused);
    $("backbtn").disabled = !s.onBreak;
  }
  // Every member's own buttons: queue yourself, or end your own update.
  const meName = (s.order || []).find(m => m.name.toLowerCase() === (me || "").toLowerCase());
  $("mybtns").classList.toggle("hidden", !meName || s.kind !== "updates" || !!s.onBreak);
  if (meName) {
    $("mynext").classList.toggle("hidden", meName.done || meName.current);
    $("mydone").classList.toggle("hidden", !meName.current);
  }
  // Recording: anyone can take it off the record; a moderator puts it back on.
  const rec = s.recording;
  $("recbadge").className = "recbadge " + (rec || "");
  $("recbadge").textContent = rec === "on" ? "● Recording" : rec === "paused" ? "Off the record" : "";
  $("recpanel").classList.toggle("hidden", rec === "off" || (rec === "paused" && !isCtl));
  $("recbtn").textContent = rec === "on" ? "Go off the record" : "Back on the record";
  $("recbtn").dataset.cmd = rec === "on" ? "co off" : "co on";
  const curId = !s.onBreak && s.speaker ? identityOf(s.speaker) : null;
  if (curId !== lastCur) render();
}

// The timeline: what we're on and who's up, the clock, the agenda as one bar (each item as wide
// as its planned minutes, filling as it runs), and in the updates the members in order.
// Everyone sees how long is left in the agenda item and when the meeting ends. How long is left
// in one person's update is shown only to that person and the moderators: never a countdown
// on someone in front of the group.
function timeline(s) {
  const now = Date.now(), mine = s.speaker && s.speaker.toLowerCase() === (me || "").toLowerCase();
  const seg = (s.agenda || []).find(a => a.state === "current");
  const segLeft = seg?.startedAt ? seg.min * 60000 - (now - seg.startedAt) : null;
  let kicker = "", who, clock = "", left = "", leftCls = "";
  if (s.ended) { who = "That's the meeting"; kicker = "Thank you"; }
  else if (s.kind === "lobby") { who = "Starting soon"; clock = fmt(s.remainingMs); left = `starts at ${clockTime(s.startAt)}`; }
  else if (s.onBreak) {
    kicker = seg ? seg.title : ""; who = "Break";
    clock = s.onBreak.until ? fmt(Math.max(0, s.onBreak.until - now)) : "☕";
    left = s.onBreak.until ? `back at ${clockTime(s.onBreak.until)}` : "back soon";
  } else {
    kicker = s.kind === "updates" && seg ? seg.title : s.kind === "updates" ? "Updates" : `${s.moderator || "The moderator"} leads`;
    who = s.kind === "updates" && s.speaker ? `${s.speaker}'s update` : s.title;
    const unitMs = s.elapsedMs + s.remainingMs;
    clock = fmt(s.elapsedMs);
    if (mine || isCtl) {
      left = s.remainingMs >= 0 ? `${fmt(s.remainingMs)} left of ${fmt(unitMs)}` : `${fmt(s.remainingMs)} over ${fmt(unitMs)}`;
      leftCls = s.remainingMs < 0 ? "over" : s.remainingMs < 120000 ? "soon" : "";
    } else left = `of ${fmt(unitMs)}`;
  }
  $("tlkicker").textContent = kicker;
  $("turnwho").textContent = who;
  $("tlclock").textContent = clock;
  $("tlleft").textContent = left;
  $("tlleft").className = "tl-left " + leftCls;
  $("timeline").classList.toggle("brk", !!s.onBreak);

  // The agenda bar. Moderators can tap an item to jump to it (two taps).
  $("tlbar").innerHTML = (s.agenda || []).map(a => {
    const c = `co goto ${a.id}`;
    let fill = a.state === "done" ? 100 : 0, over = false;
    if (a.state === "current" && a.startedAt) { const f = (now - a.startedAt) / (a.min * 60000); fill = Math.min(100, f * 100); over = f > 1; }
    const label = isArmed(c) ? "Tap again to jump here" : esc(a.title);
    const tag = isCtl && a.state !== "current" && !s.ended ? `button data-cmd="${esc(c)}" data-confirm="1"` : "div";
    const m = a.state === "done" && a.startedAt && a.endedAt ? mins(a.endedAt - a.startedAt) : a.state === "skipped" ? "skipped" : Math.round(a.min) + " min";
    return `<${tag} class="tl-seg ${a.state}${over ? " over" : ""}${isArmed(c) ? " armed" : ""}" style="flex-grow:${Math.max(1, a.min)}">` +
      `<i style="width:${fill}%"></i><span class="t">${label}</span><span class="m">${m}</span></${tag.split(" ")[0]}>`;
  }).join("");

  // The members, in order, during the updates. Moderators tap a name to make it their turn.
  const people = s.kind === "updates" || (s.onBreak && seg?.kind === "updates");
  $("tlpeople").classList.toggle("hidden", !people);
  if (people) $("tlpeople").innerHTML = (s.order || []).map(m => {
    const tag = isCtl && !m.current ? `button data-cmd="co who ${esc(m.name)}"` : "span";
    const st = m.current ? "current" : m.done ? "done" : m.present ? "" : "absent";
    const t = m.done && m.tookMs > 30000 ? ` <small>${mins(m.tookMs)}</small>` : m.current ? " <small>now</small>" : "";
    return `<${tag} class="tl-p ${st}">${m.done ? "✓ " : ""}${esc(m.name)}${t}</${tag.split(" ")[0]}>`;
  }).join("") + (s.tentative ? `<span class="tl-guess">${esc(s.tentative)}?</span>` : "");

  // Footer: the agenda item's time left and the meeting's end, for everyone; for moderators,
  // whether we're behind and any quiet suggestion.
  const bits = [];
  if (!s.ended && s.kind !== "lobby" && seg && segLeft != null)
    bits.push(segLeft >= 0 ? `${seg.title}: ${mins(segLeft)} left` : `${seg.title}: ${mins(-segLeft)} over`);
  if (!s.ended && s.kind !== "lobby" && s.next) bits.push(s.kind === "updates" ? `next: ${s.next}` : `then ${s.next}`);
  if (s.endAt && !s.ended) bits.push(`ends ${clockTime(s.endAt)}`);
  if (isCtl && !s.ended && s.kind !== "lobby") { const b = Math.round((s.behindMs || 0) / 60000); bits.push(b > 1 ? `${b} min behind` : b < -1 ? `${-b} min ahead` : "on time"); }
  if (s.muted) bits.push("Juno muted");
  if (s.paused) bits.push("Juno paused");
  $("tlfoot").textContent = bits.join(" · ");
  $("tlcue").textContent = (isCtl && s.hint) || (now - cueAt < 60000 ? cueText : "");
}

function cmd(text) {
  room?.localParticipant.publishData(enc.encode(JSON.stringify({ text })), { reliable: true, topic: "cmd", destinationIdentities: [AGENT] });
}

function banner(title, sub) {
  $("banner").innerHTML = `<span class="bt">${esc(title)}</span><span class="bs">${esc(sub || "")}</span>`;
  $("banner").classList.remove("hidden");
  clearTimeout(bannerTimer);
  bannerTimer = setTimeout(() => $("banner").classList.add("hidden"), 5000);
}

function toast(text) {
  $("toast").textContent = text;
  $("toast").classList.remove("hidden");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => $("toast").classList.add("hidden"), 8000);
}

const esc = t => String(t).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// ---------------------------------------------------------------- wiring

document.addEventListener("click", e => {
  const b = e.target.closest("button[data-cmd]");
  if (!b || !b.dataset.cmd) return;
  if (b.dataset.confirm && !isArmed(b.dataset.cmd)) { armed = { cmd: b.dataset.cmd, until: Date.now() + 3000 }; if (state) panel(); setTimeout(() => state && panel(), 3100); return; }
  armed = null;
  cmd(b.dataset.cmd);
});
$("radarform").addEventListener("submit", e => { e.preventDefault(); const v = $("radarin").value.trim(); if (v) cmd(`co radar ${v}`); $("radarin").value = ""; });
$("mic").onclick = async () => { await room.localParticipant.setMicrophoneEnabled(!room.localParticipant.isMicrophoneEnabled); render(); };
$("cam").onclick = async () => { await room.localParticipant.setCameraEnabled(!room.localParticipant.isCameraEnabled); render(); };
$("share").onclick = () => sharing() ? stopShare() : openPicker();
// "Ask Juno": hold the button (or the space bar) and speak. Whatever you say while
// holding is for the co-moderator — no wake word, and nothing else ever wakes it by accident.
let asking = false;
function ask(on) {
  if (!room || asking === on) return;
  asking = on;
  $("ask").classList.toggle("held", on);
  $("ask").textContent = on ? "🎙 Listening… release when done" : "🎙 Hold to ask Juno";
  if (on && !room.localParticipant.isMicrophoneEnabled) room.localParticipant.setMicrophoneEnabled(true).then(render);
  room.localParticipant.publishData(enc.encode(JSON.stringify({ on })), { reliable: true, topic: "ask", destinationIdentities: [AGENT] });
}
$("ask").addEventListener("pointerdown", e => { e.preventDefault(); ask(true); });
["pointerup", "pointerleave", "pointercancel"].forEach(t => $("ask").addEventListener(t, () => ask(false)));
const typing = () => ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
document.addEventListener("keydown", e => { if (e.code === "Space" && !e.repeat && !typing() && room) { e.preventDefault(); ask(true); } });
document.addEventListener("keyup", e => { if (e.code === "Space" && asking) { e.preventDefault(); ask(false); } });

$("leave").onclick = () => room?.disconnect();

// ---------------------------------------------------------------- chat
// Messages go straight to everyone in the room and nowhere else: nothing is stored, so someone
// who joins late sees only what's sent after they arrive. A message starting "Hey Juno" also
// reaches Juno as a question.
let unread = 0, chatSeen = false;
const linkify = t => esc(t).replace(/https?:\/\/[^\s<]+/g, u => `<a href="${u}" target="_blank" rel="noopener noreferrer">${u}</a>`);
function addChat(from, text, mine = false) {
  if (!text) return;
  const list = $("chatlist"), li = document.createElement("li");
  if (mine) li.className = "mine";
  li.innerHTML = `<b>${esc(from)}</b> ${linkify(text)}`;
  list.querySelector(".empty")?.remove();
  list.append(li);
  list.scrollTop = list.scrollHeight;
  if (!mine && !chatSeen) $("chatbtn").textContent = `Chat (${++unread})`;
}
new IntersectionObserver(([e]) => {
  chatSeen = e.isIntersecting;
  if (chatSeen) { unread = 0; $("chatbtn").textContent = "Chat"; }
}).observe($("chat"));
$("chatbtn").onclick = () => { $("chat").scrollIntoView({ behavior: "smooth", block: "center" }); $("chatin").focus({ preventScroll: true }); };
$("chatform").addEventListener("submit", e => {
  e.preventDefault();
  const v = $("chatin").value.trim().slice(0, 500);
  if (!v || !room) return;
  room.localParticipant.publishData(enc.encode(JSON.stringify({ text: v })), { reliable: true, topic: "chat" });
  addChat("You", v, true);
  $("chatin").value = "";
});

(async () => {
  const who = hash.get("t") ? tokenClaims(hash.get("t")).name : q.get("name");
  $("joinwho").textContent = who ? `Joining as ${who}` : "Open the room from your personal invite link.";
  // Join works straight away: it must not wait for the camera prompt (Safari holds it until answered).
  let prev = null, joining = false;
  preview().then(s => { if (joining) s?.getTracks().forEach(t => t.stop()); else prev = s; });
  $("joinbtn").onclick = () => { joining = true; join(prev).catch(e => { $("joinwho").textContent = `Couldn't join: ${e.message}`; }); };
  if (q.has("auto")) $("joinbtn").click();
})();
